import { useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { ImageUpload } from "./ImageUpload";
import {
  useCreateCameraPlan,
  useCameraPlans,
  useCopyCameraPlan,
  useUploadCameraPlanImage,
} from "@/hooks/useCameraPlans";
import { CopyFromProjectSelector } from "@/components/project-detail/CopyFromProjectSelector";
import type { CopyDateAnchors } from "@/lib/copy-from-project";
import { toast } from "sonner";

interface CreateCameraPlanDialogProps {
  projectId: string;
  previousEditionProjectId: string | null;
  projectDates: CopyDateAnchors;
  copyMode?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (id: string) => void;
}

export function CreateCameraPlanDialog({
  projectId,
  previousEditionProjectId,
  projectDates: _projectDates,
  copyMode = false,
  open,
  onOpenChange,
  onCreated,
}: CreateCameraPlanDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mapType, setMapType] = useState<"image" | "embed">("image");
  const [eventDate, setEventDate] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [embedUrl, setEmbedUrl] = useState("");
  const [sourceProjectId, setSourceProjectId] = useState<string | null>(
    previousEditionProjectId,
  );
  const [sourcePlanId, setSourcePlanId] = useState("");

  const createPlan = useCreateCameraPlan();
  const copyCameraPlan = useCopyCameraPlan();
  const uploadImage = useUploadCameraPlanImage();
  const { data: sourcePlans = [], isLoading: sourcePlansLoading } =
    useCameraPlans(sourceProjectId ?? undefined);
  const sourcePlan = sourcePlans.find((plan) => plan.id === sourcePlanId);

  const isSubmitting = createPlan.isPending || uploadImage.isPending;

  useEffect(() => {
    if (!open) return;
    setSourceProjectId(previousEditionProjectId);
    setSourcePlanId("");
  }, [open, previousEditionProjectId]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    try {
      const plan = await createPlan.mutateAsync({
        projectID: projectId,
        name: name.trim(),
        description: description.trim() || null,
        mapType: mapType,
        imageURL: null,
        embedURL: mapType === "embed" ? embedUrl.trim() || null : null,
        mapCenterLng: null,
        mapCenterLat: null,
        mapZoom: null,
        eventDate: eventDate || null,
        sortOrder: 0,
      });

      if (pendingFile && mapType === "image") {
        await uploadImage.mutateAsync({
          cameraPlanId: plan.id,
          projectId,
          file: pendingFile,
        });
      }

      resetForm();
      onOpenChange(false);
      onCreated?.(plan.id);
    } catch {
      // Error handled by mutation hooks
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setEventDate("");
    setMapType("image");
    setPendingFile(null);
    setEmbedUrl("");
    setSourceProjectId(previousEditionProjectId);
    setSourcePlanId("");
  };

  const handleCopy = async () => {
    if (!sourcePlan) return;

    try {
      const copiedPlan = await copyCameraPlan.mutateAsync({
        targetProjectId: projectId,
        sourcePlanId: sourcePlan.id,
      });
      resetForm();
      onOpenChange(false);
      onCreated?.(copiedPlan.id);
    } catch (error) {
      toast.error(
        `Failed to copy camera plan: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetForm();
        onOpenChange(isOpen);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        data-testid="create-camera-plan-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {copyMode ? "Copy Camera Plan" : "New Camera Plan"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border p-3 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Copy className="h-4 w-4" />
              Copy from
            </div>
            <div className="space-y-2">
              <Label>Project</Label>
              <CopyFromProjectSelector
                value={sourceProjectId}
                onChange={(projectId) => {
                  setSourceProjectId(projectId);
                  setSourcePlanId("");
                }}
                currentProjectId={projectId}
                placeholder="Select source project"
                testId="copy-camera-source-project"
              />
            </div>
            <div className="space-y-2">
              <Label>Camera plan</Label>
              <Select
                value={sourcePlanId}
                onValueChange={setSourcePlanId}
                disabled={!sourceProjectId || sourcePlansLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      sourcePlansLoading
                        ? "Loading plans..."
                        : "Select camera plan"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {sourcePlans.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              offlineEdit
              variant="outline"
              onClick={handleCopy}
              disabled={!sourcePlan || isSubmitting || copyCameraPlan.isPending}
              className="w-full"
              data-testid="copy-camera-plan-submit"
            >
              Copy from
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan-name">Name</Label>
            <Input
              id="plan-name"
              data-testid="camera-plan-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Day 1, Main Stage..."
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan-event-date">Event Date</Label>
            <DatePicker
              id="plan-event-date"
              data-testid="camera-plan-event-date-input"
              value={eventDate}
              onChange={setEventDate}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan-description">Description</Label>
            <Textarea
              id="plan-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={mapType}
              onValueChange={(v) => setMapType(v as "image" | "embed")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="image">
                  Image (Floor Plan / Venue Map)
                </SelectItem>
                <SelectItem value="embed">
                  Embed (Figma, Google Maps, ...)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mapType === "image" && (
            <div className="space-y-2">
              <Label>Floor Plan Image</Label>
              <ImageUpload
                onUpload={(file) => setPendingFile(file)}
                imageUrl={pendingFile ? URL.createObjectURL(pendingFile) : null}
                onRemove={() => setPendingFile(null)}
                className="h-40"
              />
            </div>
          )}

          {mapType === "embed" && (
            <div className="space-y-2">
              <Label htmlFor="embed-url">Embed URL</Label>
              <Input
                id="embed-url"
                value={embedUrl}
                onChange={(e) => setEmbedUrl(e.target.value)}
                placeholder="https://www.figma.com/file/... or Google Maps URL"
              />
              <p className="text-xs text-muted-foreground">
                Paste a Figma share link, Google Maps URL, or any embeddable
                URL. You can also add this later.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            offlineEdit
            onClick={handleSubmit}
            disabled={!name.trim() || isSubmitting}
            data-testid="create-camera-plan-submit"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
